/* Fixed desktop magnification for the existing MyArt PDP viewers. */
(() => {
  if (window.MyArtPdpViewerZoom) return;
  const desktop = matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const selector = [
    'crochet-product-media-modal', 'punch-needle-product-media-modal',
    'pn-photo-product-media-modal', 'woven-bags-product-media-modal',
    'pbn-main-media-modal', 'diamond-main-media-modal',
    'ai-pbn-product-media-modal', 'pyp-product-media-modal'
  ].map(name => `product-modal.${name}`).join(',');
  const instances = new Map();
  const clamp = value => Math.max(0, Math.min(1, value));

  function mount(modal) {
    if (instances.has(modal) || !modal.matches(selector)) return;
    const content = modal.querySelector('[role="document"]');
    const dialog = modal.querySelector('[role="dialog"]');
    if (!content || !dialog) return;
    const controller = new AbortController();
    const { signal } = controller;
    const originals = new Map();
    let zoom = null;
    let press = null;
    let tap = null;
    let frame = 0;

    function current(image) {
      return desktop.matches && modal.hasAttribute('open')
        && !modal.hasAttribute('data-myart-modal-closing')
        && image instanceof HTMLImageElement && image.parentElement === content
        && image.hasAttribute('data-media-id') && image.classList.contains('active')
        && image.getAttribute('aria-hidden') !== 'true' && !image.hasAttribute('inert');
    }

    function reset() {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      if (!zoom) return;
      zoom.layer.remove();
      zoom.image.removeAttribute('data-myart-zoomed');
      zoom.image.setAttribute('aria-pressed', 'false');
      zoom = null;
    }

    function restore(image, attrs) {
      for (const [name, value] of Object.entries(attrs)) {
        if (value === null) image.removeAttribute(name);
        else image.setAttribute(name, value);
      }
      image.removeAttribute('data-myart-zoom-image');
      image.removeAttribute('data-myart-zoomed');
    }

    function sync() {
      if (zoom && (!current(zoom.image) || !zoom.image.isConnected
        || zoom.source !== zoom.image.getAttribute('src')
        || zoom.sourceSet !== zoom.image.getAttribute('srcset'))) reset();
      for (const [image, attrs] of originals) {
        if (!current(image)) {
          restore(image, attrs);
          originals.delete(image);
        }
      }
      if (!desktop.matches || !modal.hasAttribute('open')) return;
      for (const image of content.querySelectorAll(':scope > img[data-media-id]')) {
        if (!current(image) || originals.has(image)) continue;
        const attrs = {};
        for (const name of ['role', 'tabindex', 'aria-label', 'aria-pressed']) attrs[name] = image.getAttribute(name);
        originals.set(image, attrs);
        image.setAttribute('data-myart-zoom-image', '');
        image.setAttribute('role', 'button');
        image.setAttribute('tabindex', '0');
        image.setAttribute('aria-label', `${image.alt || 'Product image'} — toggle zoom`);
        image.setAttribute('aria-pressed', 'false');
      }
    }

    function paint() {
      frame = 0;
      if (!zoom) return;
      const { rect, width, height, x, y, enlarged } = zoom;
      const left = width > rect.width ? -(width - rect.width) * x : (rect.width - width) / 2;
      const top = height > rect.height ? -(height - rect.height) * y : (rect.height - height) / 2;
      enlarged.style.transform = `translate3d(${left}px, ${top}px, 0)`;
    }

    function point(event) {
      if (!zoom) return;
      zoom.x = clamp((event.clientX - zoom.rect.left) / zoom.rect.width);
      zoom.y = clamp((event.clientY - zoom.rect.top) / zoom.rect.height);
      if (!frame) frame = requestAnimationFrame(paint);
    }

    function toggle(image, event) {
      if (!current(image)) return;
      if (zoom) { reset(); return; }
      if (!image.complete || !image.naturalWidth || !image.naturalHeight) return;
      const rect = image.getBoundingClientRect();
      const host = dialog.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const fit = Math.min(rect.width / image.naturalWidth, rect.height / image.naturalHeight);
      const width = image.naturalWidth * fit * 2;
      const height = image.naturalHeight * fit * 2;
      const layer = document.createElement('div');
      layer.className = 'myart-pdp-zoom';
      layer.setAttribute('aria-hidden', 'true');
      layer.style.left = `${rect.left - host.left}px`;
      layer.style.top = `${rect.top - host.top}px`;
      layer.style.width = `${rect.width}px`;
      layer.style.height = `${rect.height}px`;
      layer.style.borderRadius = getComputedStyle(image).borderRadius;
      layer.style.backgroundColor = getComputedStyle(modal).backgroundColor;
      const enlarged = document.createElement('img');
      enlarged.alt = '';
      enlarged.draggable = false;
      enlarged.src = image.currentSrc || image.src;
      enlarged.style.width = `${width}px`;
      enlarged.style.height = `${height}px`;
      layer.append(enlarged);
      dialog.append(layer);
      zoom = { image, layer, enlarged, rect, width, height, x: 0.5, y: 0.5,
        source: image.getAttribute('src'), sourceSet: image.getAttribute('srcset') };
      image.setAttribute('data-myart-zoomed', '');
      image.setAttribute('aria-pressed', 'true');
      if (event && event.detail !== 0) {
        zoom.x = clamp((event.clientX - rect.left) / rect.width);
        zoom.y = clamp((event.clientY - rect.top) / rect.height);
      }
      paint();
    }

    content.addEventListener('pointerdown', event => {
      tap = null;
      press = current(event.target) && event.pointerType === 'mouse' && event.button === 0
        ? { image: event.target, x: event.clientX, y: event.clientY, id: event.pointerId } : null;
    }, { signal });
    content.addEventListener('pointerup', event => {
      if (!current(event.target) || event.pointerType !== 'mouse' || event.button !== 0) return;
      // The native modal dismisses mouse pointerup. Only the active photo owns this exception.
      event.stopPropagation();
      tap = press && press.image === event.target && press.id === event.pointerId
        && Math.hypot(event.clientX - press.x, event.clientY - press.y) < 6 ? event.target : null;
      press = null;
    }, { signal });
    content.addEventListener('pointercancel', () => { press = null; tap = null; }, { signal });
    content.addEventListener('click', event => {
      if (!current(event.target) || (event.detail !== 0 && tap !== event.target)) return;
      tap = null;
      toggle(event.target, event);
    }, { signal });
    content.addEventListener('pointermove', event => {
      if (zoom && event.pointerType === 'mouse') point(event);
    }, { passive: true, signal });
    content.addEventListener('dragstart', event => {
      if (current(event.target)) event.preventDefault();
    }, { signal });
    content.addEventListener('keydown', event => {
      if (!current(event.target) || !['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      if (!event.repeat) toggle(event.target);
    }, { signal });
    content.addEventListener('scroll', reset, { passive: true, signal });
    modal.addEventListener('click', event => {
      if (zoom && event.target.closest('button, a')) reset();
    }, { capture: true, signal });
    modal.addEventListener('keydown', event => {
      if (['Escape', 'ArrowLeft', 'ArrowRight'].includes(event.key)) reset();
      if (['ArrowLeft', 'ArrowRight'].includes(event.key) && current(event.target)) {
        // The current photo becomes inert when the native viewer changes frames.
        modal.querySelector('[id^="ModalClose-"]')?.focus({ preventScroll: true });
      }
    }, { capture: true, signal });
    window.addEventListener('resize', reset, { passive: true, signal });
    desktop.addEventListener('change', () => { reset(); sync(); }, { signal });

    const stateObserver = new MutationObserver(sync);
    stateObserver.observe(modal, { attributes: true, attributeFilter: ['open', 'data-myart-modal-closing'] });
    const mediaObserver = new MutationObserver(sync);
    mediaObserver.observe(content, { subtree: true, childList: true, attributes: true,
      attributeFilter: ['class', 'aria-hidden', 'inert', 'src', 'srcset'] });
    instances.set(modal, () => {
      reset();
      controller.abort();
      stateObserver.disconnect();
      mediaObserver.disconnect();
      for (const [image, attrs] of originals) restore(image, attrs);
      instances.delete(modal);
    });
    sync();
  }

  function scan() {
    for (const [modal, destroy] of instances) if (!modal.isConnected) destroy();
    document.querySelectorAll(selector).forEach(mount);
  }
  window.MyArtPdpViewerZoom = { scan };
  function start() {
    scan();
    // ProductModal moves itself to body; observing direct children also covers editor replacement.
    new MutationObserver(scan).observe(document.body, { childList: true });
    document.addEventListener('shopify:section:load', scan);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
